import { createAsyncThunk } from '@reduxjs/toolkit';
import expenseService from '../../services/expenseService';
import localDB from '../../services/localDB';
import offlineManager from '../../services/offlineManager';

// ─── Pending-expense queue (offline) ─────────────────────────────────────────
// Expenses created while offline are queued in localDB meta and flushed at the
// next online sync. Dedup: each queued expense carries a localId; the backend
// stores it in expenses.local_id (unique index) and returns the existing row
// on a retry — a lost response can never create a duplicate expense.

const pendingExpensesKey = (branchId) => `pendingExpenses_${branchId}`;

const generateExpenseLocalId = () =>
  `local_exp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const isNetworkFailure = (error) => !error?.response;

async function queuePendingExpense(branchId, expensePayload) {
  const existing = (await localDB.getMeta(pendingExpensesKey(branchId)).catch(() => null)) || [];
  const updated = [...existing.filter((e) => e.localId !== expensePayload.localId), expensePayload];
  await localDB.setMeta(pendingExpensesKey(branchId), updated);
  console.warn('[expenses] queued offline', {
    branchId,
    localId: expensePayload.localId,
    title: expensePayload.title,
    price: expensePayload.price,
    pendingCount: updated.length,
  });
}

/**
 * Push queued offline expenses to the server. Called at the start of every
 * online sync (SyncProgressModal) and on POS boot. Safe to call repeatedly —
 * the backend dedups on localId.
 */
export const flushPendingExpenses = async (branchId) => {
  if (!branchId) return { flushed: 0, remaining: 0 };
  try {
    const pending = (await localDB.getMeta(pendingExpensesKey(branchId)).catch(() => null)) || [];
    if (pending.length === 0) return { flushed: 0, remaining: 0 };
    const remaining = [];
    let flushed = 0;
    for (const exp of pending) {
      try {
        await expenseService.createExpense(exp);
        flushed += 1;
      } catch (err) {
        // 4xx = the server rejected it (bad data) — keep it out of an
        // infinite retry loop but log loudly so it's visible in app_logs.
        const status = err?.response?.status;
        if (typeof status === 'number' && status >= 400 && status < 500) {
          console.error('[expenses] pending expense REJECTED by server — dropping from queue', {
            branchId, localId: exp.localId, title: exp.title, price: exp.price, status,
            serverMsg: err?.response?.data?.message,
          });
        } else {
          remaining.push(exp); // network/5xx — retry next sync
        }
      }
    }
    if (remaining.length > 0) {
      await localDB.setMeta(pendingExpensesKey(branchId), remaining);
    } else {
      await localDB.setMeta(pendingExpensesKey(branchId), []);
    }
    if (flushed > 0 || remaining.length > 0) {
      console.warn('[expenses] flushed pending queue', { branchId, flushed, remaining: remaining.length });
    }
    return { flushed, remaining: remaining.length };
  } catch (err) {
    console.warn('[expenses] flush failed (non-fatal)', { branchId, errMsg: String(err?.message || err) });
    return { flushed: 0, remaining: -1 };
  }
};

/**
 * Fetch expenses for a branch
 */
export const fetchExpenses = createAsyncThunk(
	'expenses/fetchExpenses',
	async ({ branchId, timeRange = '30d', filters = {} }, { rejectWithValue }) => {
		try {
			const response = await expenseService.getBranchExpenses(branchId, timeRange, filters);
			return response.data || response || [];
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch expenses');
		}
	}
);

/**
 * Fetch daily expenses for analytics
 */
export const fetchDailyExpenses = createAsyncThunk(
	'expenses/fetchDailyExpenses',
	async ({ branchId, timeRange = '180d', date, fromDate, toDate }, { rejectWithValue }) => {
		try {
			const options = (fromDate && toDate) ? { fromDate, toDate } : (date ? { date } : timeRange);
			const response = await expenseService.getDailyExpenses(branchId, options);
			// Handle both { success: true, data: [...] } and direct array responses
			const expenses = response.data || response || [];
			return expenses;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch daily expenses');
		}
	}
);

/**
 * Create a new expense.
 * Offline (or server unreachable): queue it locally with a localId and return
 * an optimistic row so the UI shows it immediately; the queue flushes at the
 * next online sync, deduped server-side on localId.
 */
export const createExpense = createAsyncThunk(
	'expenses/createExpense',
	async (expenseData, { rejectWithValue }) => {
		const payload = { ...expenseData, localId: expenseData.localId || generateExpenseLocalId() };
		const branchId = payload.branchId || payload.branch_id;

		const queueAndReturnOptimistic = async () => {
			await queuePendingExpense(branchId, payload);
			return {
				...payload,
				_id: payload.localId,
				id: payload.localId,
				status: 'pending',
				_isLocal: true,
			};
		};

		// Known-offline: don't even attempt the POST.
		if (offlineManager.shouldUseLocal() && branchId) {
			return queueAndReturnOptimistic();
		}

		try {
			const response = await expenseService.createExpense(payload);
			return response.data || response;
		} catch (error) {
			// Network-class failure (no response): fall back to the offline queue.
			// A 4xx/5xx means the server answered — surface its error instead.
			if (isNetworkFailure(error) && branchId) {
				console.warn('[expenses] createExpense server unreachable — queuing locally', {
					branchId, localId: payload.localId, title: payload.title,
				});
				return queueAndReturnOptimistic();
			}
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create expense');
		}
	}
);

/**
 * Update an existing expense
 */
export const updateExpense = createAsyncThunk(
	'expenses/updateExpense',
	async ({ expenseId, updateData }, { rejectWithValue }) => {
		try {
			const response = await expenseService.updateExpense(expenseId, updateData);
			return response.data || response;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update expense');
		}
	}
);

/**
 * Delete an expense
 */
export const deleteExpense = createAsyncThunk(
	'expenses/deleteExpense',
	async (expenseId, { rejectWithValue }) => {
		try {
			await expenseService.deleteExpense(expenseId);
			return expenseId;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete expense');
		}
	}
);

/**
 * Fetch expense categories
 */
export const fetchExpenseCategories = createAsyncThunk(
	'expenses/fetchCategories',
	async (_, { rejectWithValue }) => {
		try {
			const categories = await expenseService.getExpenseCategories();
			return categories;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch expense categories');
		}
	}
);

/**
 * Fetch expense statistics
 */
export const fetchExpenseStats = createAsyncThunk(
	'expenses/fetchStats',
	async ({ branchId, timeRange = '30d' }, { rejectWithValue }) => {
		try {
			const stats = await expenseService.getExpenseStats(branchId, timeRange);
			return stats;
		} catch (error) {
			return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch expense statistics');
		}
	}
);
